import {
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { shuffle } from "../../helpers/shuffle";
import { usePrevious } from "../../hooks/usePrevious";
import { ButtonLarge } from "../Button";
import { GameContainerProps } from "../Game/Game.container";
import GameRound, { Props as GameRoundProps } from "../GameRound";
import { AbsoluteCentered } from "../Layout";
import NewRoundVideo from "../NewRoundVideo";

export type Props = {
  readonly gameRounds: readonly GameRoundProps[];
  readonly isMuted: boolean;
} & Pick<GameContainerProps, "bgMusicRef">;

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
const GameRounds = ({
  gameRounds,
  isMuted,
  bgMusicRef,
}: Props): ReactElement | null => {
  const [hasWon, setHasWon] = useState(false);
  const [isTransitioningToNewRound, setIsTransitioningToNewRound] =
    useState(false);
  const [localGameRounds, setLocalGameRounds] = useState<Props["gameRounds"]>(
    shuffle(gameRounds)
  );
  const [currentRoundIndex, setCurrentRoundIndex] = useState(0);
  const nextRoundVidRef: RefObject<HTMLVideoElement> = useRef(null);
  const prevRoundIndex = usePrevious(currentRoundIndex);

  const currentRound = useMemo(
    () => localGameRounds[currentRoundIndex],
    [currentRoundIndex, localGameRounds]
  );

  useEffect(() => {
    if (currentRoundIndex === 0 && prevRoundIndex > 0) {
      setLocalGameRounds(shuffle(gameRounds));
    }
  }, [currentRoundIndex, gameRounds, prevRoundIndex]);

  const onWinRound = useCallback(() => {
    setHasWon(true);
  }, []);

  const onNextRound = useCallback(() => {
    setCurrentRoundIndex((prevState) =>
      prevState + 1 === gameRounds.length ? 0 : prevState + 1
    );
    setHasWon(false);
  }, [gameRounds.length]);

  const startNewRoundVideo = useCallback(() => {
    setIsTransitioningToNewRound(true);
    void (async () => {
      try {
        await nextRoundVidRef?.current?.play();
      } catch {
        onNextRound();
      }
    })();
  }, [onNextRound, nextRoundVidRef]);

  const handleEndNewRoundVideo = useCallback(() => {
    onNextRound();
    setIsTransitioningToNewRound(false);

    if (!!nextRoundVidRef.current) {
      nextRoundVidRef.current.pause();
      nextRoundVidRef.current.currentTime = 0;
    }
  }, [onNextRound, nextRoundVidRef]);

  const isVideoVisible = isTransitioningToNewRound && hasWon;

  if (!currentRound) return null;

  return (
    <div>
      <GameRound
        {...currentRound}
        onWinRound={onWinRound}
        isMuted={isMuted || isVideoVisible}
        bgMusicRef={bgMusicRef}
      />

      <>
        {hasWon && (
          <AbsoluteCentered>
            <ButtonLarge onClick={startNewRoundVideo}>
              Start nieuw woord
            </ButtonLarge>
          </AbsoluteCentered>
        )}
        <NewRoundVideo
          onEnded={handleEndNewRoundVideo}
          ref={nextRoundVidRef}
          isVisible={isVideoVisible}
        />
      </>
    </div>
  );
};

export default GameRounds;
